import React from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Gift, LogOut, Target, Wallet } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { getAccountMenuItems } from '../../lib/accountMenu';
import { adminShortcutVisible } from '../../lib/adminDomain';
import { ACCOUNT_PAGE_ATTRIBUTE, HOOKS, hook } from '../../lib/domHooks';
import { accountPageKey } from '../../lib/pageKey';

const formatMoney = (value) => `${Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} ₺`;

/**
 * Hesap sayfalarının kart açıklamaları.
 *
 * Referans arayüzde her hesap kartı başlığın altında ne işe yaradığını da
 * yazar; menü listesi (`lib/accountMenu.js`) yalnızca etiket taşıdığı için
 * açıklamalar burada durur. Karşılığı olmayan bir madde açıklamasız çizilir
 * — uydurma bir cümle yazmaktansa boş bırakmak yeğdir.
 */
const CARD_NOTES = {
  '/hesap/profil': 'Kişisel bilgileriniz ve iletişim tercihleriniz',
  '/hesap/guvenlik': 'Şifre ve iki adımlı doğrulama ayarları',
  '/hesap/kyc': 'Kimlik belgelerinizi gönderin, hesabınızı doğrulayın',
  '/hesap/mesajlar': 'Size özel duyurular ve destek yanıtları',
  '/hesap/para-yatir': 'Hesabınıza bakiye yükleyin',
  '/hesap/para-cek': 'Kazancınızı banka hesabınıza aktarın',
  '/hesap/islemler': 'Hesabınızdaki sistem düzenlemeleri',
  '/hesap/bonuslarim': 'Çevriminiz, freespin ve freebet haklarınız',
  '/promosyon-kodu': 'Kod kullanın, geçmiş kullanımlarınızı görün',
  '/hesap/kayip-bonusu': 'Kayıp iadesi talebiniz ve geçmişi',
  '/hesap/affiliate': 'Ortaklık kazancınız ve referans bağlantınız',
  '/hesap/oyun-gecmisi': 'Oynadığınız turlar ve sonuçları',
  '/hesap/bahis-kuponlari': 'Spor kuponlarınız ve durumları',
  '/admin': 'Site yönetimi',
};

/**
 * Vega şemasının hesap kabuğu.
 *
 * Diğer şemalarda hesap sayfası **sol menü + içerik** düzenindedir
 * (`pages/AccountLayout.jsx`). Referans arayüz bunu farklı kurar ve fark
 * yalnızca renk/köşe değil **düzenin kendisidir**, bu yüzden şema burada
 * bilinçli olarak çatallanır — Herkül'ün spor sayfasındaki istisnanın
 * aynısı:
 *
 *   * `/hesap` kökü bir **kart ızgarasıdır**: her hesap sayfası büyük bir
 *     kart olur, sağ üstünde soluk bir ikon durur ve üzerine gelince kart
 *     açılır. Bu ızgara aynı zamanda kökün içeriğidir — ortak düzende o
 *     adres boş bir sağ sütun gösteriyordu (`/hesap` altında `index`
 *     rotası yok).
 *   * Alt sayfada ızgaranın yerini **kırıntı yolu** alır ve içerik tam
 *     genişlikte açılır; sol menü hiç çizilmez.
 *
 * Veri ve mantık ortaktır: menü maddeleri yine `getAccountMenuItems()`'tan
 * gelir (KYC doğrulanmışsa düşer, affiliate yetkisi yoksa görünmez, spor
 * kapalıysa kupon sekmesi gizlenir) ve alt sayfaların kendisi tek koddur.
 *
 * `children`, hesap bölümünün **dışındaki** bir rotanın bu kabukta çizilmesi
 * içindir (Promosyon Kodu, bkz. App.js `PromoCodeRoute`); ortak kabuktaki
 * karşılığının aynısıdır. Verilmezse iç içe rotanın sayfası (`<Outlet />`)
 * çizilir — atlanırsa o rota bu şemada boş bir kabuk gösterirdi.
 */
const VegaAccount = ({ children = null }) => {
  const {
    user, balance, bonusBalance, logout,
  } = useAuth();
  const settings = useSettings();
  const nav = useNavigate();
  const location = useLocation();

  const items = getAccountMenuItems(user, {
    sportsEnabled: settings.sports_enabled !== false,
    adminVisible: adminShortcutVisible(settings),
  });

  // Kök adres ızgarayı gösterir; alt sayfada ızgara yerine kırıntı yolu
  // çizilir. Sondaki eğik çizgi elle girilen adreslerde de geliyor.
  const path = location.pathname.replace(/\/+$/, '');
  // Hesap dışı bir sayfa bu kabukta çizilirken (`children`) ızgara değil o
  // sayfa görünmelidir; yoksa kök ızgara sayfanın yerine geçerdi.
  const atRoot = !children && (path === '/hesap' || path === '');
  const active = items.find((item) => item.to === path);

  const wagerRequired = Number(user?.wager_required || 0);
  const wagerCompleted = Number(user?.wager_completed || 0);
  const wagerRemaining = Number(user?.wager_remaining || 0);
  const wagerProgress = wagerRequired > 0
    ? Math.min(100, (wagerCompleted / wagerRequired) * 100)
    : 100;

  return (
    <div
      className="vega-account"
      {...hook(HOOKS.ACCOUNT)}
      {...{ [ACCOUNT_PAGE_ATTRIBUTE]: atRoot ? 'home' : accountPageKey(path) }}
    >
      {/* Üstteki şerit: kimlik, iki cüzdan ve varsa kalan çevrim. Referansta
          hesap sayfasının tepesinde de bakiye durur — oyuncu para yatırmadan
          önce ne kadarı olduğunu görmelidir. */}
      <div className="vega-account-head">
        <div className="vega-account-user">
          <span className="vega-account-avatar">{user.initials}</span>
          <span className="vega-account-name">{user.username}</span>
        </div>
        <div className="vega-account-wallets">
          <span className="vega-account-wallet">
            <Wallet size={15} />
            <span>
              <em>Bakiye</em>
              <b>{formatMoney(balance)}</b>
            </span>
          </span>
          <span className="vega-account-wallet">
            <Gift size={15} />
            <span>
              <em>Bonus</em>
              <b>{formatMoney(bonusBalance)}</b>
            </span>
          </span>
        </div>
        <button
          type="button"
          className="vega-btn vega-btn-flat vega-account-logout"
          onClick={() => { logout(); nav('/'); }}
        >
          <LogOut size={15} />
          <span className="vega-btn-text">Çıkış Yap</span>
        </button>
      </div>

      {wagerRequired > 0 && (
        <div className="vega-account-wager">
          <div className="vega-account-wager-head">
            <Target size={14} />
            <span>Kalan Çevrim</span>
            <b>{formatMoney(wagerRemaining)}</b>
          </div>
          <div className="vega-account-wager-track">
            <span style={{ width: `${wagerProgress}%` }} />
          </div>
          <div className="vega-account-wager-note">
            {formatMoney(wagerCompleted)} / {formatMoney(wagerRequired)} tamamlandı
          </div>
        </div>
      )}

      {atRoot ? (
        <>
          <h1 className="vega-account-title">Hesabım</h1>
          <div className="vega-account-grid">
            {items.map((item) => (
              <NavLink key={item.to} to={item.to} className="vega-account-card">
                <item.icon className="vega-account-card-art" aria-hidden="true" />
                <span className="vega-account-card-title">{item.label}</span>
                {CARD_NOTES[item.to] && (
                  <span className="vega-account-card-note">{CARD_NOTES[item.to]}</span>
                )}
              </NavLink>
            ))}
          </div>
        </>
      ) : (
        <>
          <nav className="vega-crumbs" aria-label="Kırıntı yolu">
            <NavLink to="/hesap">Hesabım</NavLink>
            <ChevronRight size={13} aria-hidden="true" />
            <span>{active?.label || 'Detay'}</span>
          </nav>
          {children || <Outlet />}
        </>
      )}
    </div>
  );
};

export default VegaAccount;
